import { useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { fiatToBCH, formatBCH, formatFiat, getCurrencySymbol, calcTax } from '@/lib/bch';
import { NumPad } from './NumPad';
import { MenuOrderView } from './MenuOrderView';
import { QuickAmounts } from './QuickAmounts';
import { cn } from '@/lib/utils';

type CheckoutMode = 'keypad' | 'menu';

export function CheckoutView() {
  const { settings, setView, setFiatAmount, setPendingBCH, setTipAmount, setDiscount, setNote, note, products, cart, cartTotal, clearCart, setPayWith } = usePOS();
  const [mode, setMode] = useState<CheckoutMode>('keypad');
  const [rawInput, setRawInput] = useState('');
  const isOnline = useOnlineStatus();
  const { data: priceData, isLoading: priceLoading, isError: priceError } = useBCHPrice(settings.currency);

  const currencySymbol = getCurrencySymbol(settings.currency);
  const hasProducts = products.some((p) => p.available);

  // Keypad mode: use typed amount
  const parsedFiat = rawInput ? parseFloat(rawInput) || 0 : 0;
  const { taxAmount, total: parsedTotal } = calcTax(parsedFiat, settings.taxRate);
  const bchAmount = priceData ? fiatToBCH(parsedTotal, priceData.price) : 0;
  const displayAmount = rawInput || '0';

  const handleDigit = (d: string) => {
    if (d === '.') {
      if (rawInput.includes('.')) return;
      setRawInput((prev) => (prev === '' ? '0.' : prev + '.'));
      return;
    }
    if (rawInput.includes('.')) {
      const decimals = rawInput.split('.')[1];
      if (decimals && decimals.length >= 2) return;
    }
    const intPart = rawInput.split('.')[0];
    if (!rawInput.includes('.') && intPart.length >= 7) return;
    setRawInput((prev) => {
      if (prev === '0' && d !== '.') return d;
      return prev + d;
    });
  };

  const handleBackspace = () => setRawInput((prev) => prev.slice(0, -1));
  const handleClear = () => setRawInput('');

  const handleCreatePayment = () => {
    if (parsedFiat <= 0) return;
    if (!settings.bchAddress) { setView('settings'); return; }
    setTipAmount(0);
    setDiscount(0, '');
    setPayWith('BCH'); // default each new sale to BCH; customer can switch at payment
    setFiatAmount(parsedFiat);
    setPendingBCH(bchAmount);
    // Keypad mode: clear any leftover cart so it doesn't leak into the transaction
    clearCart();
    setView('discount');
  };

  const handleQuickAmount = (amount: number) => {
    setRawInput(amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
  };

  const isReady = parsedFiat > 0 && !!settings.bchAddress;

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Mode toggle — only show when products exist */}
      {hasProducts && (
        <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-1 shrink-0">
          <button
            onClick={() => { setMode('keypad'); clearCart(); }}
            className={cn(
              'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all',
              mode === 'keypad'
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
                : 'text-zinc-400 dark:text-zinc-500'
            )}
          >
            🔢 Keypad
          </button>
          <button
            onClick={() => setMode('menu')}
            className={cn(
              'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all relative',
              mode === 'menu'
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
                : 'text-zinc-400 dark:text-zinc-500'
            )}
          >
            🛍️ Menu
            {cart.length > 0 && mode !== 'menu' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0AC18E] text-white text-[10px] font-black rounded-full flex items-center justify-center">
                {cart.reduce((s, i) => s + i.qty, 0)}
              </span>
            )}
          </button>
        </div>
      )}

      {/* ── KEYPAD MODE ── */}
      {mode === 'keypad' && (
        <>
          {/* Amount Display */}
          <div className="bg-white dark:bg-zinc-800 rounded-3xl shadow-sm border border-zinc-100 dark:border-zinc-700 overflow-hidden shrink-0">
            {/* Branded header */}
            <div className="px-6 pt-5 pb-4 text-center" style={{ backgroundColor: settings.brandColor ?? '#0AC18E' }}>
              {settings.logoDataUrl ? (
                <div className="flex justify-center mb-2">
                  <img
                    src={settings.logoDataUrl}
                    alt={settings.merchantName || 'Logo'}
                    className="h-12 max-w-[160px] object-contain"
                  />
                </div>
              ) : (
                <p className="text-lg font-black text-white mb-0.5">
                  {settings.merchantName || 'BCHPAY.Cash'}
                </p>
              )}
              {settings.merchantTagline && (
                <p className="text-xs text-white/70 font-medium">{settings.merchantTagline}</p>
              )}
            </div>
            <div className="p-6 pt-4 text-center">
            <div className="flex items-baseline justify-center gap-2 min-h-[64px]">
              <span className="text-3xl font-medium text-zinc-400 dark:text-zinc-500">{currencySymbol}</span>
              <span className={cn(
                'font-bold tabular-nums transition-all',
                displayAmount.length > 6 ? 'text-5xl' : 'text-6xl',
                parsedFiat > 0 ? 'text-zinc-900 dark:text-zinc-50' : 'text-zinc-300 dark:text-zinc-600'
              )}>
                {displayAmount}
              </span>
            </div>
            <div className="mt-3 space-y-1">
              {priceLoading && !priceData && <p className="text-sm text-zinc-400 animate-pulse">Loading BCH price...</p>}
              {priceError && !priceData && <p className="text-sm text-red-400">Price unavailable</p>}
              {settings.taxRate > 0 && parsedFiat > 0 && (
                <div className="text-xs text-zinc-400 space-y-0.5">
                  <div className="flex justify-center gap-3">
                    <span>Subtotal: {formatFiat(parsedFiat, settings.currency)}</span>
                    <span>{settings.taxName} ({settings.taxRate}%): {formatFiat(taxAmount, settings.currency)}</span>
                  </div>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200">
                    Total: {formatFiat(parsedTotal, settings.currency)}
                  </p>
                </div>
              )}
              {priceData && parsedFiat > 0 && (
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  <p className="text-sm font-mono font-medium text-[#0AC18E]">≈ {formatBCH(bchAmount)} BCH</p>
                  {(priceData.stale || !isOnline) && (
                    <span className="text-[10px] text-amber-500 font-semibold bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 px-1.5 py-0.5 rounded-full">
                      cached price
                    </span>
                  )}
                </div>
              )}
              {priceData && parsedFiat === 0 && (
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  <p className="text-xs text-zinc-400 dark:text-zinc-600">
                    1 BCH = {formatFiat(priceData.price, settings.currency)}
                  </p>
                  {(priceData.stale || !isOnline) && (
                    <span className="text-[10px] text-amber-500 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 px-1.5 py-0.5 rounded-full font-semibold">
                      cached
                    </span>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Quick amounts (Pro) */}
          <QuickAmounts onSelect={handleQuickAmount} />

          {/* NumPad */}
          <div className="flex-1">
            <NumPad onDigit={handleDigit} onBackspace={handleBackspace} onClear={handleClear} />
          </div>

          {/* Note input */}
          <div className="shrink-0">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (table #, order name…)"
              maxLength={80}
              className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-700 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] focus:border-transparent"
            />
          </div>

          {/* CTA */}
          <button
            onClick={handleCreatePayment}
            disabled={!isReady}
            className={cn(
              'w-full py-5 rounded-2xl text-lg font-bold transition-all duration-150 select-none shrink-0',
              isReady
                ? 'bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 active:scale-98 hover:bg-[#09AF81]'
                : parsedFiat > 0 && !settings.bchAddress
                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
            )}
          >
            {parsedFiat > 0 && !settings.bchAddress
              ? '⚙️ Set BCH Address First'
              : parsedFiat > 0
              ? 'Charge →'
              : 'Enter Amount'}
          </button>
        </>
      )}

      {/* ── MENU MODE ── */}
      {mode === 'menu' && (
        <>
          {/* Cart summary banner (when items in cart but drawer not open) */}
          {cart.length > 0 && (
            <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 rounded-2xl px-4 py-2.5 flex items-center justify-between border border-[#0AC18E]/20 shrink-0">
              <span className="text-sm font-medium text-zinc-600 dark:text-zinc-300">
                {cart.reduce((s, i) => s + i.qty, 0)} item{cart.reduce((s, i) => s + i.qty, 0) !== 1 ? 's' : ''} in order
              </span>
              <div className="flex items-center gap-3">
                <span className="text-base font-bold text-[#0AC18E]">
                  {currencySymbol}{calcTax(cartTotal, settings.taxRate).total.toFixed(2)}
                </span>
                <button
                  onClick={clearCart}
                  className="text-xs text-zinc-400 hover:text-red-400 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 min-h-0">
            <MenuOrderView />
          </div>
        </>
      )}
    </div>
  );
}
